import { env } from "@/shared/constants/env";
import { apiClient } from "../../../shared/api/client";
import { generateIdempotencyKey } from "../../../shared/api/idempotency";
import type { components } from "../../../shared/api/generated";
import type { DeliveryAddress, Order, OrderItem, OrderStatus } from "../../../entities/order";
import { isActiveStatus, isFailedStatus, isFinalStatus } from "../../../shared/format/orderStatus";

// Toutes les mutations qui modifient l'état d'une commande envoient une
// Idempotency-Key — protège contre les doubles débits si une requête timeout
// puis est retentée (manuellement ou par le réseau).
function idempotencyHeaders() {
  return { headers: { "Idempotency-Key": generateIdempotencyKey() } };
}

// ─── DTOs alignés sur openapi.json ───────────────────────────────────────────

export type CreateOrderItem = {
  name:             string;
  quantity:         number;
  estimated_price?: number;
};

export type CreateOrderPayload = {
  items:             CreateOrderItem[];
  delivery_address:  DeliveryAddress;
  pickup_location?:  {
    name?:      string;
    latitude?:  number;
    longitude?: number;
  };
  estimated_amount:  number;
  distance_km?:      number;
  is_emergency?:     boolean;
  emergency_type?:   string | null;
};

export type OrdersListResponse = {
  data:      Order[];
  total:     number;
  page?:     number;
  per_page?: number;
};

export type OrderTransitionTarget =
  components["schemas"]["Order"]["status"] extends infer S
    ? S extends "AGENT_EN_ROUTE" | "PURCHASING" | "PURCHASED" | "DELIVERING" | "DELIVERED" | "DISPUTED"
      ? S
      : never
    : never;

export type ItemValidationStatus = NonNullable<OrderItem["validation_status"]>;

// ─── Endpoints ────────────────────────────────────────────────────────────────

export async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const { data } = await apiClient.post<Order>("/orders", payload, idempotencyHeaders());
  return data;
}

export type ListOrdersParams = {
  status?:   OrderStatus;
  page?:     number;
  per_page?: number;
  agent_id?: string;
};

// ⚠️ L'intercepteur de client.ts DÉBALLE l'enveloppe { data, meta } : `response.data`
// devient l'array d'Order (comme listAgents/listTrips) et la pagination est posée
// sur `response.meta`. On reconstruit ici { data, total } pour les consommateurs
// (useOrders.select, agentMissionService) qui lisent `.data`. Sans ce déballage,
// `res.data` valait undefined → listes vides (commandes client + missions agent).
//
// Le backend a aussi livré des réponses paginées en DOUBLE enveloppe
// ({ data: { data: [...], total } }) : l'intercepteur ne retire que le 1er niveau
// et `res.data` est alors un OBJET — sans la normalisation ci-dessous, les
// consommateurs crashaient (« history.filter is not a function »).
export async function listOrders(params: ListOrdersParams = {}): Promise<OrdersListResponse> {
  const res   = await apiClient.get<Order[]>("/orders", { params });
  const body  = res.data as unknown;
  const inner = (body as { data?: unknown } | null)?.data;
  const data: Order[] = Array.isArray(body) ? body : Array.isArray(inner) ? (inner as Order[]) : [];
  if (env.isDev && body != null && !Array.isArray(body) && !Array.isArray(inner)) {
    console.warn("[orders] GET /orders — forme de réponse inattendue (traitée comme vide):", JSON.stringify(body).slice(0, 200));
  }
  const meta  = (res as { meta?: { total?: number } }).meta;
  const total = meta?.total ?? (body as { total?: number } | null)?.total ?? data.length;
  return { data, total };
}

export async function getOrder(orderId: string): Promise<Order> {
  const { data } = await apiClient.get<Order>(`/orders/${orderId}`);
  return data;
}

export async function cancelOrder(orderId: string, reason?: string): Promise<Order> {
  const { data } = await apiClient.post<Order>(
    `/orders/${orderId}/cancel`,
    { reason },
    idempotencyHeaders(),
  );
  return data;
}

export async function transitionOrder(
  orderId: string,
  toStatus: OrderTransitionTarget,
  reason?: string,
): Promise<Order> {
  const { data } = await apiClient.post<Order>(
    `/orders/${orderId}/transition`,
    { status: toStatus, reason },
    idempotencyHeaders(),
  );
  return data;
}

export type ValidateItemPayload = {
  validation_status: ItemValidationStatus;
  final_price?:      number;
  note?:             string;
};

export async function validateOrderItem(
  orderId: string,
  itemId:  string,
  payload: ValidateItemPayload,
): Promise<OrderItem> {
  const { data } = await apiClient.post<OrderItem>(
    `/orders/${orderId}/items/${itemId}/validation`,
    payload,
    idempotencyHeaders(),
  );
  return data;
}

// SUPERVISOR only — finalize and capture payment
export async function finalizeOrder(orderId: string, finalAmount?: number): Promise<Order> {
  const { data } = await apiClient.post<Order>(
    `/orders/${orderId}/finalize`,
    { final_amount: finalAmount },
    idempotencyHeaders(),
  );
  return data;
}

// ─── Helpers de filtrage côté client ─────────────────────────────────────────
// Utilisé par les écrans qui veulent grouper Active / Done / Cancelled
// sans faire 3 requêtes.

export function categorizeOrder(order: Order): "active" | "done" | "cancelled" {
  const s = order.status;
  if (!s)                return "active";
  if (isFailedStatus(s)) return "cancelled";
  if (isFinalStatus(s))  return "done";
  if (isActiveStatus(s)) return "active";
  return "active";
}
